import {
  AnalyticsCategory,
  AnalyticsName,
  EcommerceEventProduct,
  NavigationEventProduct,
} from "@storebrand-digital/insurance-analytics";
import {
  googleAnalyticsCategoryOf,
  googleAnalyticsNameOf,
} from "Utils/analytics/analyticsMappings";

import { AddProduct } from "Store/addProductToCart/interfaces";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { BrandModelAndYear } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseVehicleProduct";
import { isDefined } from "Utils/functions";
import { positiveNumberOr0 } from "Utils/generalUtils";

export const ecommerceEventProductsFrom = (
  products: InsuranceProduct[] | AddProduct[],
): EcommerceEventProduct[] =>
  products.map((product) => ({
    name: name(product),
    coverage: coverage(product),
    productId: product.productId,
    agreementCode: agreementCode(product),
    category: category(product),
    yearlyPrice: yearlyPrice(product),
    yearlyPriceWithTax: yearlyPriceWithTax(product),
    totalYearlyDiscount: totalYearlyDiscount(product),
    supplementaryInfo: supplementaryInfo(product),
  }));

export const navigationEventProductsFrom = (
  products: InsuranceProduct[] | AddProduct[],
): NavigationEventProduct[] =>
  products.map((product) => ({
    name: name(product),
    category: category(product),
    supplementaryInfo: supplementaryInfo(product),
  }));

export const supplementaryInfo = (
  info?: InsuranceProduct | AddProduct | BrandModelAndYear,
): string | undefined => {
  if (info && "brand" in info && "model" in info && "modelYear" in info) {
    return `${info.brand} ${info.model} ${info.modelYear}`;
  }
  return undefined;
};

const name = (product: InsuranceProduct | AddProduct): AnalyticsName =>
  "name" in product
    ? googleAnalyticsNameOf(product.name)
    : googleAnalyticsNameOf(product.type);

const coverage = (product: InsuranceProduct | AddProduct): string =>
  "coverageGroup" in product
    ? product.coverageGroup ?? ""
    : (product as AddProduct)?.coverage;

const agreementCode = (
  product: InsuranceProduct | AddProduct,
): string | undefined => {
  if (!("priceSpecifications" in product)) {
    return undefined;
  }
  return product.priceSpecifications?.discount?.agreementProduct
    ? `${product.priceSpecifications?.discount?.agreementProduct}`
    : undefined;
};

const category = (
  product: InsuranceProduct | AddProduct,
): AnalyticsCategory | undefined =>
  "name" in product
    ? googleAnalyticsCategoryOf(product.name)
    : googleAnalyticsCategoryOf(product.type);

const yearlyPrice = (product: InsuranceProduct | AddProduct): number =>
  "priceSpecifications" in product
    ? product.priceSpecifications.price.perYearPrecise || 0
    : product.priceData.yearlyPricePrecise || 0;

const yearlyPriceWithTax = (product: InsuranceProduct | AddProduct): number => {
  const yearlyPrice =
    "priceSpecifications" in product
      ? product.priceSpecifications.price.perYearPrecise || 0
      : product.priceData.yearlyPricePrecise || 0;
  const tffTax =
    "trafikkforsikringsavgift" in product &&
    isDefined(product.trafikkforsikringsavgift)
      ? product.trafikkforsikringsavgift.yearlyAmount
      : 0;
  return yearlyPrice + tffTax;
};

const totalYearlyDiscount = (product: InsuranceProduct | AddProduct): number =>
  "priceSpecifications" in product
    ? product.priceSpecifications.price.yearlyDiscount ?? 0
    : positiveNumberOr0(
        (product.priceData.fullYearlyPricePrecise ?? 0) -
          product.priceData.yearlyPricePrecise,
      );
